import { fabric } from 'fabric';
import type { MutableRefObject } from 'react';
import { clearPhotoZoneHighlight, highlightSelectedPhotoZone } from './highlight-photo-zone';
import { resolvePhotoZoneIdFromEvent } from './resolve-photo-zone-id-from-event';

/**
 * PRCM-10916: Handles a `dragover` event over the canvas while a photo-tray
 * thumbnail is being dragged, driving the live drag-over highlight.
 *
 * `dragover` fires continuously as the pointer moves, so this resolves the
 * hovered photo zone and only re-highlights (and triggers a canvas render)
 * when the hovered zone id actually CHANGES from the last call — avoiding
 * redundant highlight/render work on every mouse-move tick.
 *
 * The drag-over highlight intentionally allows already-occupied zones
 * (`allowOccupied: true`) since a drag-to-canvas drop replaces the zone's
 * current image in place, mirroring the click-to-fill replace behavior.
 *
 * @param canvas - The fabric canvas being dragged over
 * @param event - The fabric `dragover` event
 * @param lastHoveredZoneIdRef - Ref tracking the previously hovered zone id,
 * mutated in place so subsequent calls can detect a change
 * @returns The resolved photo zone id (or `null` if not hovering any zone)
 */
export const handlePhotoZoneDragOver = (
  canvas: fabric.Canvas | null | undefined,
  event: fabric.IEvent<Event>,
  lastHoveredZoneIdRef: MutableRefObject<string | null>,
): string | null => {
  // Required for the browser to allow a subsequent `drop` event to fire.
  event.e.preventDefault();

  const hoveredZoneId = resolvePhotoZoneIdFromEvent(canvas, event);

  if (hoveredZoneId === lastHoveredZoneIdRef.current) {
    return hoveredZoneId;
  }

  lastHoveredZoneIdRef.current = hoveredZoneId;
  highlightSelectedPhotoZone(canvas, hoveredZoneId, true);

  return hoveredZoneId;
};

/**
 * PRCM-10916: Clears the transient drag-over highlight and resets the
 * hovered-zone tracking ref. This never mutates card/selection state — it
 * only re-renders the canvas overlay.
 *
 * Behavior differs by outcome:
 * - On a CANCELLED drag (`dragleave`), pass `restoreSelection: true` (default)
 *   so the persistent click-selection highlight for `selectedZoneId` is
 *   re-painted — the user keeps their prior zone selection.
 * - On a SUCCESSFUL `drop`, pass `restoreSelection: false` so NO overlay is
 *   restored. Restoring here would re-paint (and `bringToFront`) the
 *   click-selection overlay on top of the freshly dropped image, producing a
 *   confusing translucent/dashed layer over the new photo. Instead the drop
 *   caller selects the just-filled zone (via `onZoneFilled`); that is
 *   overlay-safe because a filled zone has content and the selection highlight
 *   only paints empty zones, so no overlay is re-added on a later render.
 *
 * @param canvas - The fabric canvas to clear/restore highlighting on
 * @param lastHoveredZoneIdRef - Ref tracking the previously hovered zone id, reset to null
 * @param selectedZoneId - The currently click-selected photo zone id, or null/undefined
 * @param restoreSelection - Whether to re-paint the persistent selection highlight (default true)
 */
export const clearPhotoZoneDragHighlight = (
  canvas: fabric.Canvas | null | undefined,
  lastHoveredZoneIdRef: MutableRefObject<string | null>,
  selectedZoneId: string | null | undefined,
  restoreSelection = true,
): void => {
  clearPhotoZoneHighlight(canvas);
  lastHoveredZoneIdRef.current = null;
  if (restoreSelection) {
    highlightSelectedPhotoZone(canvas, selectedZoneId ?? null);
  }
};
